import { memo, useMemo, useRef } from 'react';
import { useTheme, Paper, Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

const AXIS_FONT_SIZE = 12;
const CHARTS_WEIGHT = 3;

// ─── Типы ────────────────────────────────────────────────────────────────────

export interface DepthPoint {
    /** Глубина, м (отрицательное значение, напр. -12) */
    depth: number;
    /** Температура, °С */
    temp: number;
}

export interface DepthChartProps {
    points: DepthPoint[];
    /** Высота графика в пикселях или процентах (напр. 400 или "100%"). По умолчанию 400. */
    height?: number | `${number}%`;
    /**
     * Вызывается при движении мыши над графиком.
     * Передаёт индекс точки в исходном массиве `points`, к глубине которой
     * притянулся курсор, или `null` при потере фокуса.
     */
    onDepthHover?: (index: number | null) => void;
}

// ─── Внутренний тип с сохранённым исходным индексом ─────────────────────────

interface SortedPoint extends DepthPoint {
    _originalIndex: number;
}

// ─── Подсказка ───────────────────────────────────────────────────────────────

interface TooltipContentProps {
    active?: boolean;
    payload?: { payload: SortedPoint }[];
}

function DepthTooltip({ active, payload }: TooltipContentProps) {
    const theme = useTheme();
    if (!active || !payload?.length) return null;
    const { depth, temp } = payload[0].payload;
    return (
        <Paper
            elevation={3}
            sx={{
                px: 1.5,
                py: 1,
                border: `1px solid ${theme.palette.primary.dark}`,
                pointerEvents: 'none',
            }}
        >
            <Typography variant="caption" display="block" color="text.primary">
                глубина: {depth} м
            </Typography>
            <Typography variant="caption" display="block" color="text.primary">
                температура: {temp.toFixed(2)} °С
            </Typography>
        </Paper>
    );
}

// ─── Вспомогательная функция: красивый шаг для оси ───────────────────────────

function niceStep(range: number, targetDivisions = 5): number {
    const rough = range / targetDivisions;
    for (const s of [0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100]) {
        if (s >= rough) return s;
    }
    return 1;
}

// ─── Компонент ───────────────────────────────────────────────────────────────

export default memo(function DepthChart({ points, height = 400, onDepthHover }: DepthChartProps) {
    const theme = useTheme();
    const chartHeight = height;

    const lastFiredIndex = useRef<number | null>(null);

    const sorted = useMemo<SortedPoint[]>(
        () => [...points].map((p, i) => ({ ...p, _originalIndex: i })).sort((a, b) => a.depth - b.depth),
        [points],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMouseMove = (state: any) => {
        // activeTooltipIndex — индекс ближайшей точки по Y-позиции, надёжнее чем
        // activePayload (тот заполняется только вблизи самой линии)
        const idx = state?.activeTooltipIndex as number | undefined;
        if (idx == null || idx < 0) return;
        const point = sorted[idx];
        if (!point) return;
        const originalIndex = point._originalIndex;
        if (originalIndex !== lastFiredIndex.current) {
            lastFiredIndex.current = originalIndex;
            onDepthHover?.(originalIndex);
        }
    };

    const handleMouseLeave = () => {
        if (lastFiredIndex.current !== null) {
            lastFiredIndex.current = null;
            onDepthHover?.(null);
        }
    };

    // Тики оси Y: целые метры от 0 вниз до минимальной глубины.
    // Сетка будет с шагом 1 м, выровненная по 0.
    const yTicks = useMemo<number[]>(() => {
        const minDepth = sorted.length > 0 ? Math.floor(sorted[0].depth) : 0;
        const ticks: number[] = [];
        for (let d = 0; d >= minDepth; d--) ticks.push(d);
        return ticks;
    }, [sorted]);

    const { xTicks, xDomain } = useMemo<{ xTicks: number[]; xDomain: [number, number] }>(() => {
        if (!sorted.length) return { xTicks: [-1, 0, 1], xDomain: [-1, 1] };
        const temps = sorted.map((p) => p.temp);
        const loRaw = Math.min(...temps);
        const hiRaw = Math.max(...temps);
        const step = niceStep(Math.max(hiRaw - loRaw, 0.1));
        const lo = Math.floor(loRaw / step) * step;
        const hi = Math.ceil(hiRaw / step) * step;
        const ticks: number[] = [];
        for (let t = lo; t <= hi + step * 0.001; t += step) {
            ticks.push(Math.round(t / step) * step);
        }
        return { xTicks: ticks, xDomain: [lo, hi] };
    }, [sorted]);

    const color = theme.palette.primary.main;
    const colorLight = theme.palette.primary.light;
    const gridColor = theme.palette.divider;
    const zeroLineColor = theme.palette.divider;
    const bgColor = theme.palette.background.paper;
    const textColor = theme.palette.text.secondary;

    if (points.length === 0) {
        return (
            <Box
                sx={{
                    height,
                    display: 'flex',
                    m: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <Typography variant="body2" color="text.disabled">
                    Нет данных для отображения
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', '& *:focus': { outline: 'none' } }}>
            <Box sx={{ width: '100%', height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        layout="vertical"
                        data={sorted}
                        margin={{ top: 12, right: 16, bottom: 12, left: 4 }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

                        {/* Ось X — температура, расположена сверху */}
                        <XAxis
                            type="number"
                            orientation="top"
                            ticks={xTicks}
                            domain={xDomain}
                            tickFormatter={(v: number) => +v.toFixed(2) + ''}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            label={{
                                value: '°С',
                                position: 'insideBottomRight',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dy: -2,
                            }}
                        />

                        {/* Ось Y — глубина: 0 наверху, убывает вниз */}
                        <YAxis
                            type="number"
                            dataKey="depth"
                            ticks={yTicks}
                            domain={[yTicks[yTicks.length - 1] ?? 0, 0]}
                            reversed
                            width={38}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            tickFormatter={(v: number) => `${v}`}
                            label={{
                                value: 'м',
                                position: 'insideTopLeft',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dx: 4,
                                dy: 12,
                            }}
                        />

                        <ReferenceLine x={0} stroke={zeroLineColor} strokeWidth={1.2} />
                        <ReferenceLine y={0} stroke={zeroLineColor} strokeWidth={1.2} />

                        {/*
                         * cursor — recharts рисует горизонтальную пунктирную линию
                         *          через встроенный механизм (надёжнее ReferenceLine).
                         * activeDot — recharts выделяет ближайшую точку самостоятельно.
                         */}
                        <Tooltip
                            content={<DepthTooltip />}
                            cursor={{
                                stroke: colorLight,
                                strokeWidth: 1.5,
                                strokeDasharray: '6 4',
                                fill: 'none',
                            }}
                            wrapperStyle={{ transition: 'opacity 150ms ease' }}
                        />

                        <Line
                            type="linear"
                            dataKey="temp"
                            stroke={color}
                            strokeWidth={CHARTS_WEIGHT}
                            isAnimationActive={false}
                            dot={{ r: 4, stroke: color, strokeWidth: 1.5, fill: bgColor }}
                            activeDot={{ r: 5, stroke: color, strokeWidth: 2, fill: color }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
});
